import { type FormEvent, useState } from "react";
import {
    type CreatedMCPAPIKey,
    type MCPAPIKey,
    useCreateMCPAPIKeyMutation,
    useDeactivateMCPAPIKeyMutation,
    useGetMCPAPIKeysQuery,
} from "@/store/booking-management-api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Badge } from "@/lib/shadcn/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { Separator } from "@/lib/shadcn/ui/separator";
import { AlertTriangleIcon, CheckIcon, CopyIcon, KeyIcon, PlusIcon, TrashIcon } from "lucide-react";
import { errorPopup } from "@/utils/alerts";

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function CreateKeyDialog({ open, onClose, onSubmit, isLoading }: { open: boolean; onClose: () => void; onSubmit: (name: string) => void; isLoading: boolean }) {
    const [name, setName] = useState("");

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (name.trim()) {
            onSubmit(name.trim());
        }
    };

    const handleClose = () => {
        setName("");
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="keyName">Name</Label>
                        <Input id="keyName" placeholder="e.g. Claude Desktop" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
                        <p className="text-xs text-muted-foreground">A label to help you identify this key later.</p>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!name.trim() || isLoading}>
                            {isLoading ? "Creating..." : "Create"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function NewKeyReveal({ createdKey, onDone }: { createdKey: CreatedMCPAPIKey; onDone: () => void }) {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = async () => {
        await navigator.clipboard.writeText(createdKey.key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={true} onOpenChange={onDone}>
            <DialogContent className="sm:max-w-[540px]">
                <DialogHeader>
                    <DialogTitle>API Key Created</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <Alert>
                        <AlertTriangleIcon className="h-4 w-4" />
                        <AlertDescription>Copy this key now. You won&apos;t be able to see it again.</AlertDescription>
                    </Alert>
                    <div className="space-y-2">
                        <Label>Your API Key</Label>
                        <div className="flex gap-2">
                            <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono break-all select-all">{createdKey.key}</code>
                            <Button variant="outline" size="icon" onClick={copyToClipboard} className="shrink-0">
                                {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={onDone}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function APIKeyRow({ apiKey, onDeactivate }: { apiKey: MCPAPIKey; onDeactivate: (id: number) => void }) {
    return (
        <TableRow className={!apiKey.isActive ? "opacity-50" : ""}>
            <TableCell className="font-medium max-w-md truncate" title={apiKey.name}>
                {apiKey.name}
            </TableCell>
            <TableCell>
                <Badge variant={apiKey.isActive ? "default" : "secondary"}>{apiKey.isActive ? "Active" : "Inactive"}</Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">{formatDate(apiKey.createdAt)}</TableCell>
            <TableCell className="text-muted-foreground">{apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : "Never"}</TableCell>
            <TableCell className="text-right">
                {apiKey.isActive && (
                    <Button variant="ghost" size="sm" onClick={() => onDeactivate(apiKey.id)} className="text-destructive hover:text-destructive">
                        <TrashIcon className="h-4 w-4" />
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
}

export default function SettingsPage() {
    const { data: apiKeys, isLoading } = useGetMCPAPIKeysQuery();
    const [createKey, { isLoading: isCreating }] = useCreateMCPAPIKeyMutation();
    const [deactivateKey] = useDeactivateMCPAPIKeyMutation();

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState<CreatedMCPAPIKey | null>(null);

    const handleCreate = async (name: string) => {
        try {
            const result = await createKey({ name }).unwrap();
            setShowCreateDialog(false);
            setNewlyCreatedKey(result);
        } catch {
            errorPopup("Failed to create API key");
        }
    };

    const handleDeactivate = async (id: number) => {
        try {
            await deactivateKey(id).unwrap();
        } catch {
            errorPopup("Failed to deactivate API key");
        }
    };

    const activeKeys = apiKeys?.filter((k) => k.isActive) ?? [];
    const inactiveKeys = apiKeys?.filter((k) => !k.isActive) ?? [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">Manage your account settings and API access.</p>
            </div>

            <Separator />

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <KeyIcon className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <CardTitle>MCP API Keys</CardTitle>
                                <CardDescription>Keys for connecting MCP clients like Claude Desktop.</CardDescription>
                            </div>
                        </div>
                        <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                            <PlusIcon className="mr-1.5 h-4 w-4" />
                            Create Key
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
                    ) : apiKeys?.length === 0 ? (
                        <div className="text-center py-8">
                            <KeyIcon className="mx-auto h-8 w-8 text-muted-foreground/50 mb-3" />
                            <p className="text-sm text-muted-foreground">No API keys yet. Create one to get started.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Last Used</TableHead>
                                    <TableHead className="text-right w-[60px]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {activeKeys.map((key) => (
                                    <APIKeyRow key={key.id} apiKey={key} onDeactivate={handleDeactivate} />
                                ))}
                                {inactiveKeys.map((key) => (
                                    <APIKeyRow key={key.id} apiKey={key} onDeactivate={handleDeactivate} />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <CreateKeyDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onSubmit={handleCreate} isLoading={isCreating} />

            {newlyCreatedKey && <NewKeyReveal createdKey={newlyCreatedKey} onDone={() => setNewlyCreatedKey(null)} />}
        </div>
    );
}
